import { useHookstate } from "@hookstate/core";
import { I18n } from "aws-amplify";
import classNames from "classnames";
import moment from "moment";
import { Button } from "primereact/button";
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Dropdown } from "primereact/dropdown";
import { Sidebar } from 'primereact/sidebar';
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { useGlobalHelpState } from "../../../help-context";
import { Help } from "../../help/Help";
import InstallationCard from "../installation/InstallationCard";
import { addMeetingFromInstallation, calculateDurationBeforeAddMeeting, calculateTourneeDuration, currentTournee, parseDate, parseTime, scrbData, scrollToTop, tourneeInstallations } from "../ScrbTools";
import { globalIsAdminState } from "../../../app-context";

ManageTournee.propTypes = {
    saveTournee: PropTypes.func.isRequired,
    showNotif: PropTypes.func.isRequired,
    onprintTournee: PropTypes.func.isRequired
};

function ManageTournee(props) {

    const initialTournee = useHookstate(currentTournee);
    const installations = useHookstate(tourneeInstallations);
    const isAdmin = useHookstate(globalIsAdminState);
    const data = useHookstate(scrbData).get();
    const [firstAppointment, setFirstAppointment] = useState(null);
    const [allInstallationsAvailable, setAllInstallationsAvailable] = useState([]);
    const [tournee, setTournee] = useState();
    const [tourneeFull, setTourneeFull] = useState(false);
    const helpState = useGlobalHelpState();
    const [printMode, setPrintMode] = useState(false);

    useEffect(() => {
        setTournee(initialTournee.get());
        scrollToTop();
        helpState.set({ show: false, help: Help.manageTournee })
        // eslint-disable-next-line
    }, [])

    useEffect(() => {
        if (tournee) {
            loadInstallationsAvailable();
            setFirstAppointment(moment.utc(Math.min(...tournee.meetings.map(m => moment.utc(m.debutReception).valueOf()))));
            setTourneeFull(calculateTourneeDuration(tournee.meetings.filter(m => !m.deleted)) >= 480);
        }
        // eslint-disable-next-line
    }, [tournee]);

    const loadInstallationsAvailable = () => {
        const installationsDisponibles = installations.get().filter(i =>
            (i.statut < 3 && !tournee?.meetings.some(m => m.installationId === i.id)) ||
            (tournee?.meetings.some(m => m.installationId === i.id && m.deleted))
        );
        setAllInstallationsAvailable(installationsDisponibles);
    }

    const confirmRemoveMeeting = (meeting) => {
        confirmDialog({
            message: I18n.get("confirmRemoveReception"),
            header: 'Confirmation',
            icon: 'pi pi-exclamation-triangle',
            defaultFocus: 'accept',
            acceptLabel: I18n.get("delete"),
            rejectLabel: I18n.get("cancel"),
            accept: () => removeMeeting(meeting)
        });
    };

    const removeMeeting = (meeting) => {
        const meetings = JSON.parse(JSON.stringify(tournee?.meetings));
        const meetingIndex = meetings.findIndex(m => m.installationId === meeting.installationId);
        if (meetingIndex !== -1) {
            meetings[meetingIndex] = {
                ...meetings[meetingIndex],
                deleted: true
            };
        }
        setTournee({ ...tournee, meetings: reorder(meetings) });
    }

    const addMeeting = (installation) => {
        const newMeeting = addMeetingFromInstallation(installation);
        const meetings = JSON.parse(JSON.stringify(tournee?.meetings)) || [];
        if (isAdmin.get() || calculateDurationBeforeAddMeeting(meetings.filter(m => !m.deleted), newMeeting) <= 480) {
            if (meetings.some(m => m.installationId === newMeeting.installationId)) {
                meetings.find(m => m.installationId === newMeeting.installationId).deleted = false;
            } else
                meetings.push(newMeeting);
        }
        else props.showNotif("error", I18n.get("error"), I18n.get("tourneeFull"));
        setTournee({ ...tournee, meetings: reorder(meetings) });
    }

    const reorder = (meetings) => {
        let index = 0;
        return meetings.map((meeting) => {
            if (!meeting.deleted) {
                meeting.debutReception = moment(firstAppointment).add(index++, "hours").toDate();
            }
            return meeting;
        });
    }

    const onDragEnd = (result) => {
        const { destination, source } = result;
        if (!destination) {
            return;
        }
        if (source.index === destination.index && source.droppableId === destination.droppableId) {
            return;
        }
        const deletedMeetings = tournee?.meetings.filter(m => m.deleted);
        const meetings = JSON.parse(JSON.stringify(tournee?.meetings.filter(m => !m.deleted)));
        const meeting = meetings.splice(source.index, 1)[0];
        meetings.splice(destination.index, 0, meeting);
        meetings.forEach((meeting, index) => {
            meeting.debutReception = moment(firstAppointment).add(index, "hours").toDate();
        });
        setTournee({ ...tournee, meetings: reorder(meetings.concat(deletedMeetings)) });
    }

    const draggableStyle = (item, index) => {
        let icon;
        if (index === 0)
            icon = "fa-light fa-angle-down";
        else if (index === tournee?.meetings.length - 1)
            icon = "fa-light fa-angle-up";
        else
            icon = "fa-light fa-angles-up-down";
        return <Draggable
            key={item.installationId.toString()}
            draggableId={item.installationId.toString()}
            index={index}
        >
            {(provided, snapshot) => (<div
                className="flex pb-2"
                ref={provided.innerRef}
                {...provided.draggableProps}
                {...provided.dragHandleProps}
                style={{
                    ...provided.draggableProps.style
                }}
            >
                <InstallationCard
                    installation={item}
                    mode="tournee"
                    viewMode={tournee?.validee || tournee?.meetings.length === 1}
                    dragging={snapshot.isDragging}
                    onRemove={() => confirmRemoveMeeting(item)}
                />
                {!tournee?.validee && <span className={`flex justify-content-center align-items-center ${icon} m-1 p-1`} style={{ fontSize: '1.5rem' }} />}
            </div>
            )}
        </Draggable>
    };

    const dropDownItemTemplate = (item) => {
        return (
            <div className="flex justify-content-between flex-wrap w-25rem">
                <div className="flex">
                    <i className={data.installationColors[item.genreInstallationId].icon + " text-" + data.installationColors[item.genreInstallationId].color + "-500"} style={{ fontSize: '1.5rem' }}></i>
                </div>
                <div className="flex">
                    <div className="font-semibold pl-2">{item.nom}</div>
                </div>
                <div className="flex">
                    <div className="pl-2">{I18n.get("ref")} : {item.referenceExterne}</div>
                </div>
                <div className="flex">
                    {item.address.localite}
                </div>
            </div>
        );
    }

    const printView = () => {
        setTimeout(() => {
            window.print();
            setPrintMode(false);
        }, 1000);
        return tournee && <Sidebar visible={printMode} onHide={() => setPrintMode(false)} fullScreen>
            <div className="pl-4 pt-6">
                <h5 className="flex justify-content-between pb-2">
                    <div className="flex flex-column">
                        <span className="flex">
                            <i className="fa-light fa-calendar mr-2"></i>
                            {I18n.get("tournees") + " " + I18n.get("du") + " " + parseDate(tournee.date)}
                        </span>
                        <span className="flex">
                            <i className="fa-light fa-clock mr-2"></i>
                            {I18n.get("firstAppointment") + " : " + parseTime(tournee.meetings[0].debutReception)}
                        </span>
                    </div>
                    <div className="flex align-items-center text-3xl underline">
                        {tournee.validee ? I18n.get("confirme") : I18n.get("nonConfirme")}
                    </div>
                </h5>
                {
                    tournee?.meetings.filter(m => !m.deleted).map((item) => (
                        <InstallationCard
                            key={item.referenceExterne}
                            installation={item}
                            mode="tournee"
                            printMode={true}
                            viewMode={true}
                            dragging={false}
                        />
                    ))
                }
            </div >
        </Sidebar >
    }

    const nbRdv = () => {
        const nbRdv = tournee?.meetings?.filter(m => !m.deleted)?.length;
        const initialNbRdv = initialTournee.get()?.meetings?.length;
        if (![null, undefined].includes(nbRdv) && ![null, undefined].includes(initialNbRdv)) {
            return (
                <div>
                    {nbRdv !== initialNbRdv && <br />}
                    <span
                        className={classNames("flex",
                            { "text-red-500": nbRdv !== initialNbRdv },
                        )}
                    >
                        <i className="fa-light fa-calendar-pen mr-2"></i>
                        {I18n.get("nbRdv") + " : " + nbRdv + " / " + initialNbRdv}
                    </span>
                </div>
            )
        }
        return <></>;
    }

    return printMode ? printView() : tournee && (
        <div className="card">
            <ConfirmDialog />
            <h5 className="flex justify-content-between">
                <div className="flex flex-column">
                    <span className="flex">
                        <i className="fa-light fa-calendar mr-2"></i>
                        {I18n.get("tournees") + " " + I18n.get("du") + " " + parseDate(tournee.date)}
                    </span>
                    <span className="flex">
                        <i className="fa-light fa-clock mr-2"></i>
                        {I18n.get("firstAppointment") + " : " + parseTime(tournee.meetings[0].debutReception)}
                    </span>
                    {nbRdv()}
                </div>
            </h5>
            <hr />
            <div className="flex flex-column lg:flex-row-reverse">
                <div className="w-full lg:w-4 mb-3 lg:mb-0 lg:ml-3 order-1 lg:order-2 dynamic-filters">
                    <Dropdown
                        options={allInstallationsAvailable}
                        itemTemplate={dropDownItemTemplate}
                        optionLabel="referenceExterne"
                        onChange={(e) => addMeeting(e.value)}
                        placeholder={I18n.get("addReception")}
                        className="w-full"
                        filter
                        filterPlaceholder={I18n.get("searchRef")}
                        emptyMessage={I18n.get("noReceptionAvailable")}
                        style={{ position: 'sticky', top: '10%', alignSelf: 'flex-start' }}
                        disabled={tournee?.validee || (tourneeFull && !isAdmin.get())}
                        filterBy="nom,referenceExterne,address.localite"
                    />
                </div>
                <div className="w-full lg:w-9 order-3 lg:order-1">
                    <DragDropContext onDragEnd={onDragEnd}>
                        <Droppable droppableId="planned" isDropDisabled={tournee.validee}>
                            {provided => (
                                <div
                                    {...provided.droppableProps}
                                    ref={provided.innerRef}
                                >
                                    {tournee.meetings.filter(m => !m.deleted).map((item, index) => (
                                        draggableStyle(item, index)
                                    ))}
                                    {provided.placeholder}
                                </div>
                            )}
                        </Droppable>
                    </DragDropContext>
                </div>

            </div>
            <div className="flex flex-column lg:flex-row justify-content-between ">
                <Button
                    label={I18n.get("cancel")}
                    onClick={() => initialTournee.set({})}
                    icon="pi pi-arrow-left"
                    className="p-button-outlined m-2"
                />
                <Button
                    label={I18n.get("print")}
                    onClick={() => setPrintMode(true)}
                    icon="pi pi-print"
                    className="p-button-outlined m-2"
                />
                <Button
                    label={I18n.get("save")}
                    icon="pi pi-save"
                    onClick={() => {
                        tournee.meetings.forEach((m, idx) => {
                            if (!m.deleted) {
                                setTournee({ ...tournee, meetings: [...tournee.meetings.slice(0, idx), m, ...tournee.meetings.slice(idx + 1)] });
                            }
                        });
                        props.saveTournee(tournee);
                    }}
                    disabled={
                        tournee.meetings.filter(m => !m.deleted).length === 0
                        || tournee?.validee
                        || (tournee.meetings.filter(m => !m.deleted).length !== initialTournee.get().meetings.length
                            && !isAdmin.get())
                    }
                    className="p-button-outlined m-2"
                />
            </div>
        </div >
    );
}

export default ManageTournee;
